import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ANLAGEN_TYPEN, ANLAGEN_ART, ANREDE, STATUS_LABELS, api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, FileCheck2, X, Plus, Trash2, Wand2 } from "lucide-react";
import { detectNetzbetreiber } from "@/lib/netzbetreiber";
import { ExistingCustomerHint } from "@/components/kunden/ExistingCustomerHint";

const empty = {
  kunde_anrede: "herr",
  kunde_vorname: "",
  kunde_nachname: "",
  kunde_strasse: "",
  kunde_plz: "",
  kunde_ort: "",
  anlagen_typen: [],
  anlagen_art: "neue_anlage",
  leistung_kw: "",
  anmeldedatum: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })(),
  fertigmeldungdatum: "",
  inbetriebnahmedatum: "",
  auftraggeber_id: "none",
  netzbetreiber: "",
  zaehlernummer: "",
  installateur: "",
  modul_hersteller: "",
  modul_typ: "",
  wechselrichter_hersteller: "",
  wechselrichter_typ: "",
  speicher_hersteller: "",
  speicher_typ: "",
  notizen: "",
  status: "angemeldet",
  zaehlerabmeldung: false,
  zaehlerabmeldung_nummern: [],
  document_path: null,
  document_name: null,
  projekt_id: "none",
};

export function AnmeldungForm({ open, onOpenChange, initial, onSaved, auftraggeber = [] }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const isEdit = Boolean(initial?.id);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      const seed = initial ? { ...empty, ...initial } : empty;
      if (!seed.auftraggeber_id) seed.auftraggeber_id = "none";
      if (!Array.isArray(seed.anlagen_typen)) seed.anlagen_typen = [];
      if (!Array.isArray(seed.zaehlerabmeldung_nummern)) {
        seed.zaehlerabmeldung_nummern = seed.zaehlerabmeldung_nummer ? [seed.zaehlerabmeldung_nummer] : [];
      }
      if (seed.fertigmeldungdatum == null) seed.fertigmeldungdatum = "";
      if (seed.inbetriebnahmedatum == null) seed.inbetriebnahmedatum = "";
      if (!seed.kunde_anrede) seed.kunde_anrede = "herr";
      if (!seed.projekt_id) seed.projekt_id = "none";
      setForm(seed);
    }
  }, [open, initial]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Netzbetreiber-Vorschlag: erst VNBdigital (Backend), dann lokaler PLZ-Fallback
  const [remoteSuggest, setRemoteSuggest] = useState(null);
  const localSuggest = detectNetzbetreiber(form.kunde_plz, form.kunde_ort);
  const suggestedNetzbetreiber = remoteSuggest || localSuggest;
  const suggestSource = remoteSuggest ? "VNBdigital" : "Anschrift";
  const showNetzbetreiberSuggest =
    suggestedNetzbetreiber && suggestedNetzbetreiber !== form.netzbetreiber;

  const applyNetzbetreiber = () => {
    if (suggestedNetzbetreiber) update("netzbetreiber", suggestedNetzbetreiber);
  };

  // Bei PLZ-Wechsel (5-stellig) VNBdigital-Lookup starten
  useEffect(() => {
    if (!open) return;
    const plz = String(form.kunde_plz || "").trim();
    if (plz.length !== 5 || !/^\d{5}$/.test(plz)) { setRemoteSuggest(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/lookup/netzbetreiber`, { params: { plz, ort: form.kunde_ort || "" } });
        if (cancelled) return;
        const nb = data.netzbetreiber;
        setRemoteSuggest(nb || null);
        // Automatisch übernehmen — VNBdigital ist die verlässlichste Quelle
        if (nb) setForm((f) => ({ ...f, netzbetreiber: nb }));
      } catch {
        if (!cancelled) setRemoteSuggest(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.kunde_plz, open]);

  // Fallback: lokale Erkennung wenn Netzbetreiber leer und remote nichts liefert
  useEffect(() => {
    if (!open) return;
    if (form.netzbetreiber) return;
    if (remoteSuggest) return;
    if (localSuggest) setForm((f) => (f.netzbetreiber ? f : { ...f, netzbetreiber: localSuggest }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.kunde_plz, form.kunde_ort, open, remoteSuggest]);

  const toggleTyp = (t) =>
    setForm((f) => ({
      ...f,
      anlagen_typen: f.anlagen_typen.includes(t)
        ? f.anlagen_typen.filter((x) => x !== t)
        : [...f.anlagen_typen, t],
    }));

  const addZaehler = () => setForm((f) => ({ ...f, zaehlerabmeldung_nummern: [...f.zaehlerabmeldung_nummern, ""] }));
  const setZaehler = (i, v) =>
    setForm((f) => ({
      ...f,
      zaehlerabmeldung_nummern: f.zaehlerabmeldung_nummern.map((x, idx) => (idx === i ? v : x)),
    }));
  const removeZaehler = (i) =>
    setForm((f) => ({
      ...f,
      zaehlerabmeldung_nummern: f.zaehlerabmeldung_nummern.filter((_, idx) => idx !== i),
    }));

  const handleExtract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Datei zu groß (max. 15 MB)"); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/documents/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        kunde_anrede: ex.kunde_anrede || f.kunde_anrede,
        kunde_vorname: ex.kunde_vorname || f.kunde_vorname,
        kunde_nachname: ex.kunde_nachname || f.kunde_nachname,
        kunde_strasse: ex.kunde_strasse || f.kunde_strasse,
        kunde_plz: ex.kunde_plz || f.kunde_plz,
        kunde_ort: ex.kunde_ort || f.kunde_ort,
        anlagen_typen: Array.isArray(ex.anlagen_typen) && ex.anlagen_typen.length ? ex.anlagen_typen : f.anlagen_typen,
        anlagen_art: ex.anlagen_art || f.anlagen_art,
        leistung_kw: ex.leistung_kw ? String(ex.leistung_kw) : f.leistung_kw,
        netzbetreiber: ex.netzbetreiber || f.netzbetreiber,
        zaehlernummer: ex.zaehlernummer || f.zaehlernummer,
        installateur: ex.installateur || f.installateur,
        modul_hersteller: ex.modul_hersteller || f.modul_hersteller,
        modul_typ: ex.modul_typ || f.modul_typ,
        wechselrichter_hersteller: ex.wechselrichter_hersteller || f.wechselrichter_hersteller,
        wechselrichter_typ: ex.wechselrichter_typ || f.wechselrichter_typ,
        speicher_hersteller: ex.speicher_hersteller || f.speicher_hersteller,
        speicher_typ: ex.speicher_typ || f.speicher_typ,
        zaehlerabmeldung: Array.isArray(ex.zaehlerabmeldung_nummern) && ex.zaehlerabmeldung_nummern.length ? true : f.zaehlerabmeldung,
        zaehlerabmeldung_nummern: Array.isArray(ex.zaehlerabmeldung_nummern) && ex.zaehlerabmeldung_nummern.length
          ? ex.zaehlerabmeldung_nummern
          : f.zaehlerabmeldung_nummern,
        notizen: ex.notizen || f.notizen,
        document_path: data.document_path,
        document_name: data.document_name,
      }));
      toast.success("Daten aus Dokument extrahiert — bitte prüfen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraktion fehlgeschlagen");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.anlagen_typen.length === 0) {
      toast.error("Bitte mindestens einen Anlagentyp auswählen");
      return;
    }
    if (!form.kunde_nachname && !form.kunde_vorname) {
      toast.error("Bitte Vor- und/oder Nachname angeben");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        leistung_kw: parseFloat(form.leistung_kw) || 0,
        auftraggeber_id: form.auftraggeber_id === "none" ? null : form.auftraggeber_id,
        fertigmeldungdatum: form.fertigmeldungdatum || null,
        inbetriebnahmedatum: form.inbetriebnahmedatum || null,
        zaehlerabmeldung_nummern: form.zaehlerabmeldung ? form.zaehlerabmeldung_nummern.filter(Boolean) : [],
        projekt_id: form.projekt_id === "none" ? null : form.projekt_id,
      };
      if (isEdit) {
        await api.patch(`/anmeldungen/${initial.id}`, payload);
        toast.success("Anmeldung aktualisiert");
      } else {
        await api.post("/anmeldungen", payload);
        toast.success("Anmeldung erstellt");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const hasStromspeicher = form.anlagen_typen.includes("stromspeicher");
  const hasPV = form.anlagen_typen.includes("erzeugungsanlage");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anmeldung-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            {isEdit ? "Anmeldung bearbeiten" : "Neue Netzanmeldung"}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-dashed border-primary/50 bg-primary/5 p-4" data-testid="ai-extract-box">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">Auftragsbestätigung / Rechnung hochladen</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                KI extrahiert Kunde (Anrede, Vor-/Nachname), Adresse (Straße/PLZ/Ort), Anlagentypen, Anlagenart,
                Leistung, Modul-/Wechselrichter-/Speicher-Hersteller &amp; -Typ, Zähler-Nrn.
                PDF / JPG / PNG · max. 15 MB
              </div>
              {form.document_name && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1 text-xs" data-testid="doc-attached">
                  <FileCheck2 className="h-3.5 w-3.5 text-primary" />
                  <span className="truncate max-w-[240px]">{form.document_name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setForm((f) => ({ ...f, document_path: null, document_name: null }))}
                    data-testid="doc-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <label className="shrink-0">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleExtract}
                disabled={extracting}
                data-testid="doc-upload-input"
              />
              <span
                className={`inline-flex items-center gap-2 rounded-md border border-primary bg-primary text-primary-foreground px-3 py-2 text-sm font-medium cursor-pointer hover:opacity-90 ${extracting ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="doc-upload-btn"
              >
                <Upload className="h-4 w-4" />
                {extracting ? "Analysiere …" : "Dokument"}
              </span>
            </label>
          </div>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-4 py-2">
          <div className="md:col-span-6 space-y-1.5">
            <Label>Auftraggeber</Label>
            <Select value={form.auftraggeber_id} onValueChange={(v) => update("auftraggeber_id", v)}>
              <SelectTrigger data-testid="form-auftraggeber">
                <SelectValue placeholder="Kein Auftraggeber" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="form-auftraggeber-none">— Kein Auftraggeber —</SelectItem>
                {auftraggeber.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`form-auftraggeber-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <Label>Anrede</Label>
            <Select value={form.kunde_anrede || "herr"} onValueChange={(v) => update("kunde_anrede", v)}>
              <SelectTrigger data-testid="form-kunde-anrede"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ANREDE).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`form-anrede-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label htmlFor="kunde_vorname">Vorname</Label>
            <Input id="kunde_vorname" value={form.kunde_vorname} onChange={(e) => update("kunde_vorname", e.target.value)} data-testid="form-kunde-vorname" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label htmlFor="kunde_nachname">Nachname / Firma</Label>
            <Input id="kunde_nachname" value={form.kunde_nachname} onChange={(e) => update("kunde_nachname", e.target.value)} required data-testid="form-kunde-nachname" />
          </div>

          <div className="md:col-span-6 space-y-1.5">
            <Label htmlFor="kunde_strasse">Straße &amp; Hausnr.</Label>
            <Input id="kunde_strasse" value={form.kunde_strasse} onChange={(e) => update("kunde_strasse", e.target.value)} required data-testid="form-kunde-strasse" />
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <Label htmlFor="kunde_plz">PLZ</Label>
            <Input id="kunde_plz" value={form.kunde_plz} onChange={(e) => update("kunde_plz", e.target.value)} data-testid="form-kunde-plz" />
          </div>
          <div className="md:col-span-4 space-y-1.5">
            <Label htmlFor="kunde_ort">Ort</Label>
            <Input id="kunde_ort" value={form.kunde_ort} onChange={(e) => update("kunde_ort", e.target.value)} data-testid="form-kunde-ort" />
          </div>

          {!isEdit && open && (
            <ExistingCustomerHint
              className="md:col-span-6"
              data={{
                kunde_name: `${form.kunde_vorname || ""} ${form.kunde_nachname || ""}`.trim(),
                kunde_adresse: form.kunde_strasse, kunde_plz: form.kunde_plz,
              }}
              linkedId={form.projekt_id}
              onLink={(pid) => update("projekt_id", pid)}
              onUse={(h) => setForm((f) => ({
                ...f,
                kunde_anrede: h.kunde_anrede || f.kunde_anrede,
                kunde_vorname: h.kunde_nachname ? h.kunde_vorname || "" : "",
                kunde_nachname: h.kunde_nachname || h.kunde_name,
                kunde_strasse: h.strasse || f.kunde_strasse, kunde_plz: h.kunde_plz || f.kunde_plz, kunde_ort: h.kunde_ort || f.kunde_ort,
                projekt_id: f.projekt_id !== "none" ? f.projekt_id : (h.project_refs?.[0]?.id || "none"),
              }))}
            />
          )}

          <div className="md:col-span-6 space-y-1.5">
            <Label>Mit Projekt verknüpfen</Label>
            <Select value={form.projekt_id || "none"} onValueChange={(v) => update("projekt_id", v)}>
              <SelectTrigger data-testid="form-projekt-select"><SelectValue placeholder="Kein Projekt" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Kein Projekt —</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`form-projekt-${p.id}`}>
                    {p.kunde_name} · {p.project_type === "waermepumpe" ? "Wärmepumpe" : "PV"}{p.kunde_ort ? ` · ${p.kunde_ort}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-6 space-y-2">
            <Label>Anlagentypen (Mehrfachauswahl)</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ANLAGEN_TYPEN).map(([k, v]) => {
                const checked = form.anlagen_typen.includes(k);
                return (
                  <label key={k}
                    className={`flex items-center gap-2 rounded-md border p-3 cursor-pointer transition-colors ${checked ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}
                    data-testid={`typ-check-${k}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleTyp(k)} />
                    <span className="text-sm font-medium">{v}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-6 space-y-2">
            <Label>Anlagenart</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ANLAGEN_ART).map(([k, v]) => {
                const active = form.anlagen_art === k;
                return (
                  <button type="button" key={k} onClick={() => update("anlagen_art", k)}
                    className={`rounded-md border p-3 text-sm font-medium text-left transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}
                    data-testid={`art-${k}`}
                  >{v}</button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="leistung_kw">Gesamtleistung (kWp)</Label>
            <Input id="leistung_kw" type="number" step="0.01" min="0" value={form.leistung_kw} onChange={(e) => update("leistung_kw", e.target.value)} required data-testid="form-leistung" />
          </div>
          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="anmeldedatum">Anmeldedatum</Label>
            <Input id="anmeldedatum" type="date" value={form.anmeldedatum} onChange={(e) => update("anmeldedatum", e.target.value)} required data-testid="form-anmeldedatum" />
          </div>

          <div className="md:col-span-3 space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`form-status-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="fertigmeldungdatum">Fertigmeldungsdatum</Label>
            <Input id="fertigmeldungdatum" type="date" value={form.fertigmeldungdatum || ""} onChange={(e) => update("fertigmeldungdatum", e.target.value)} data-testid="form-fertigmeldungdatum" />
          </div>
          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="inbetriebnahmedatum">Inbetriebnahmedatum</Label>
            <Input id="inbetriebnahmedatum" type="date" value={form.inbetriebnahmedatum || ""} onChange={(e) => update("inbetriebnahmedatum", e.target.value)} data-testid="form-inbetriebnahmedatum" />
          </div>

          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="netzbetreiber">Netzbetreiber</Label>
            <Input id="netzbetreiber" value={form.netzbetreiber} onChange={(e) => update("netzbetreiber", e.target.value)} data-testid="form-netzbetreiber" />
            {remoteSuggest && form.netzbetreiber === remoteSuggest && (
              <div className="mt-1 text-xs text-primary inline-flex items-center gap-1" data-testid="netzbetreiber-source">
                <Wand2 className="h-3 w-3" /> Automatisch von VNBdigital übernommen
              </div>
            )}
            {showNetzbetreiberSuggest && (
              <button
                type="button"
                onClick={applyNetzbetreiber}
                className="mt-1 text-xs text-primary inline-flex items-center gap-1 hover:underline"
                data-testid="netzbetreiber-suggest"
              >
                <Wand2 className="h-3 w-3" /> Vorschlag ({suggestSource}): <span className="font-medium">{suggestedNetzbetreiber}</span>
              </button>
            )}
          </div>
          <div className="md:col-span-3 space-y-1.5">
            <Label htmlFor="zaehlernummer">Zählernummer</Label>
            <Input id="zaehlernummer" value={form.zaehlernummer} onChange={(e) => update("zaehlernummer", e.target.value)} data-testid="form-zaehlernummer" />
          </div>

          <div className="md:col-span-6 space-y-1.5">
            <Label htmlFor="installateur">Installateur</Label>
            <Input id="installateur" value={form.installateur} onChange={(e) => update("installateur", e.target.value)} data-testid="form-installateur" />
          </div>

          {/* Komponenten */}
          {hasPV && (
            <>
              <div className="md:col-span-6 pt-2 border-t border-border">
                <div className="overline">Photovoltaik-Komponenten</div>
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="modul_hersteller">Modul-Hersteller</Label>
                <Input id="modul_hersteller" value={form.modul_hersteller} onChange={(e) => update("modul_hersteller", e.target.value)} data-testid="form-modul-hersteller" />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="modul_typ">Modul-Typ</Label>
                <Input id="modul_typ" value={form.modul_typ} onChange={(e) => update("modul_typ", e.target.value)} data-testid="form-modul-typ" />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="wr_hersteller">Wechselrichter-Hersteller</Label>
                <Input id="wr_hersteller" value={form.wechselrichter_hersteller} onChange={(e) => update("wechselrichter_hersteller", e.target.value)} data-testid="form-wr-hersteller" />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="wr_typ">Wechselrichter-Typ</Label>
                <Input id="wr_typ" value={form.wechselrichter_typ} onChange={(e) => update("wechselrichter_typ", e.target.value)} data-testid="form-wr-typ" />
              </div>
            </>
          )}

          {hasStromspeicher && (
            <>
              <div className="md:col-span-6 pt-2 border-t border-border">
                <div className="overline">Batteriespeicher</div>
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="speicher_hersteller">Speicher-Hersteller</Label>
                <Input id="speicher_hersteller" value={form.speicher_hersteller} onChange={(e) => update("speicher_hersteller", e.target.value)} data-testid="form-speicher-hersteller" />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="speicher_typ">Speicher-Typ</Label>
                <Input id="speicher_typ" value={form.speicher_typ} onChange={(e) => update("speicher_typ", e.target.value)} data-testid="form-speicher-typ" />
              </div>
            </>
          )}

          {/* Zählerabmeldung */}
          <div className="md:col-span-6 rounded-md border border-border p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="zaehlerabmeldung-toggle">
              <Checkbox checked={form.zaehlerabmeldung} onCheckedChange={(v) => {
                const enabled = Boolean(v);
                update("zaehlerabmeldung", enabled);
                if (enabled && form.zaehlerabmeldung_nummern.length === 0) {
                  update("zaehlerabmeldung_nummern", [""]);
                }
              }} />
              <div>
                <div className="text-sm font-medium">Zählerabmeldung erforderlich</div>
                <div className="text-xs text-muted-foreground">Ein oder mehrere bestehende Zähler abmelden</div>
              </div>
            </label>
            {form.zaehlerabmeldung && (
              <div className="space-y-2 pl-7" data-testid="zaehlerabmeldung-fields">
                {form.zaehlerabmeldung_nummern.map((n, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      value={n}
                      onChange={(e) => setZaehler(i, e.target.value)}
                      placeholder={`Zählernummer ${i + 1}`}
                      data-testid={`zaehler-nr-${i}`}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeZaehler(i)} data-testid={`zaehler-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addZaehler} data-testid="zaehler-add">
                  <Plus className="h-4 w-4 mr-1.5" /> Weiterer Zähler
                </Button>
              </div>
            )}
          </div>

          <div className="md:col-span-6 space-y-1.5">
            <Label htmlFor="notizen">Notizen</Label>
            <Textarea id="notizen" rows={3} value={form.notizen} onChange={(e) => update("notizen", e.target.value)} data-testid="form-notizen" />
          </div>

          <DialogFooter className="md:col-span-6 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="form-submit">
              {saving ? "Speichern …" : isEdit ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
